import { useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetch3 } from '../commonApis/commonApis';
import { logout } from '../features/auth/authSlice';

export const useIdleTimeout = (idleTime = 30 * 60 * 1000) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const profile = useSelector((state: any) => state.auth.profile);
  const url = process.env.REACT_APP_BASE_URL;
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const performLogout = useCallback(async () => {
    if (!profile) return;
    try {
      await fetch3(`${url}/api/auth/logout?type=client`, "get");
    } catch (error) {
      console.error("Logout API failed", error);
    }
    dispatch(logout());
    navigate("/");
  }, [profile, url, dispatch, navigate]);

  const resetTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // Only set timeout if user is logged in
    if (profile) {
      timeoutRef.current = setTimeout(() => {
        performLogout();
      }, idleTime);
    }
  }, [performLogout, idleTime, profile]);

  useEffect(() => {
    if (!profile) return;

    // Events that represent user activity
    const events = [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mouseWheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'scroll'
    ];
    
    const handleEvent = () => resetTimer();
    
    // Add event listeners
    events.forEach((event) => 
      document.addEventListener(event, handleEvent, { passive: true })
    );
    
    // Start the initial timer
    resetTimer();

    return () => {
      // Cleanup event listeners
      events.forEach((event) => 
        document.removeEventListener(event, handleEvent)
      );
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [resetTimer, profile]);
};
